import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ShotChart from '../components/ShotChart.js';
import {
  adviceFor, daysOffRoast, filterShots, getEspressoStatus, getShot, groupByDay, guideFor, isFault, listBeans, listShots, MODE_LABEL,
  PROFILE_GUIDE, ROAST_LEVELS, saveBean, setShotMine, sortShots, tasteFor, TASTES, updateShot, type Bean, type BeanInput,
  type ProfileGuide, type Shot, type ShotDetail, type ShotSort,
} from '../lib/espresso.js';
import { dayLabel, shortDate, ymdInHomeZone } from '../lifeos/format.js';
import { useLoad } from '../lifeos/useLoad.js';
import { Button, Chip, EmptyState, Fact, Group, Loader, Page, Row, Segmented, useParamSetter, useToast } from '../ui/index.js';

const SHOTS_SHOWN = 60;

const time = (iso: string): string => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const when = (iso: string, today: string): string => `${shortDate(ymdInHomeZone(new Date(iso)), today)} ${time(iso)}`;
const secs = (ms: number): string => `${Math.round(ms / 1000)}s`;
const ago = (epochSec: unknown, today: string): string =>
  typeof epochSec === 'number' && epochSec > 0 ? shortDate(ymdInHomeZone(new Date(epochSec * 1000)), today) : 'never';
const dayOf = (iso: string): string => ymdInHomeZone(new Date(iso));

/** "★4 sweet, balanced" */
function tasteLine(s: Shot): string {
  return [s.rating !== null ? `★${s.rating}` : null, s.taste.map((k) => tasteFor(k)?.label.toLowerCase() ?? k).join(', ') || null]
    .filter(Boolean)
    .join(' ');
}

/** "Roasters Next Door · 26 days off roast" */
function beanLine(b: Bean, today: string): string {
  const days = daysOffRoast(b.roastDate, today);
  return [b.roaster, b.roastLevel, days === null ? 'roast date unknown' : `${days} days off roast`].filter(Boolean).join(' · ');
}

type Tab = 'shots' | 'beans' | 'profiles';
const TABS: { value: Tab; label: string }[] = [
  { value: 'shots', label: 'Shots' },
  { value: 'beans', label: 'Beans' },
  { value: 'profiles', label: 'Profile guide' },
];
const SORTS: { value: ShotSort; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'longest', label: 'Longest' },
  { value: 'rated', label: 'Best rated' },
];

/**
 * Coffee (`/coffee`, `?tab=beans|profiles`): shots by day with the live machine
 * beside them; beans and the profile guide on their own tabs.
 */
export default function Coffee(): JSX.Element {
  const status = useLoad(() => getEspressoStatus(), []);
  const shots = useLoad(() => listShots({ limit: 200 }), []);
  const beans = useLoad(() => listBeans(), []);
  const [params, setParam] = useParamSetter();
  const tab = (TABS.some((t) => t.value === params.get('tab')) ? params.get('tab') : 'shots') as Tab;
  const today = ymdInHomeZone(new Date());

  return (
    <Page title="Coffee" sub={status.data ? subline(status.data) : undefined}>
      <div className="cf-tabs">
        <Segmented label="Coffee" value={tab} options={TABS} onChange={(v) => setParam('tab', v === 'shots' ? null : v)} />
      </div>
      {tab === 'shots' && (
        <div className="cf-layout">
          <aside className="cf-side" aria-label="Machine">
            <Loader state={status}>
              {(st) => <MachinePanel st={st} beans={beans.data ?? []} today={today} />}
            </Loader>
          </aside>
          <div className="cf-main">
            <Loader state={shots}>
              {(items) =>
                items.length === 0 ? (
                  status.data?.configured ? <EmptyState icon="coffee" title="No shots yet." hint="Pull one — it shows up here when it finishes." /> : null
                ) : (
                  <ShotTable
                    shots={items}
                    beans={beans.data ?? []}
                    today={today}
                    onMine={async () => {
                      await Promise.all([shots.reload(), status.reload()]);
                    }}
                  />
                )
              }
            </Loader>
          </div>
        </div>
      )}
      {tab === 'beans' && (
        <Loader state={beans}>
          {(items) => (
            <Group header="Beans" footer="Taste each shot (open it) — tagged shots tell you which way to move the grind.">
              {items.map((b) => (
                <Row
                  key={b.id}
                  to={`/coffee/beans/${b.id}`}
                  tone={b.active ? 'accent' : 'default'}
                  title={b.name}
                  sub={beanLine(b, today)}
                  detail={b.active ? 'In use' : `${b.shots} shots`}
                />
              ))}
              <Row to="/coffee/beans/new" title="Add a bean" />
            </Group>
          )}
        </Loader>
      )}
      {tab === 'profiles' && <ProfileGuideGrid active={activeProfile(status.data)} />}
    </Page>
  );
}

type Status = Awaited<ReturnType<typeof getEspressoStatus>>;

/** The right-hand card: today, the machine live, the bean in the hopper, upkeep, alerts. */
function MachinePanel({ st, beans, today }: { st: Status; beans: Bean[]; today: string }): JSX.Element {
  const [alertsOpen, setAlertsOpen] = useState(false);
  if (!st.configured)
    return (
      <EmptyState
        icon="coffee"
        title="Espresso machine not connected."
        hint="Set GAGGIUINO_MQTT_URL on the server and point the machine's MQTT settings at the broker."
      />
    );
  const sensors = (st.state.sensors?.payload ?? {}) as Record<string, unknown>;
  const system = (st.state.system?.payload ?? {}) as Record<string, unknown>;
  const maint = (st.state.maintenance?.payload ?? {}) as Record<string, unknown>;
  const profile = (st.state['profile/active']?.payload ?? {}) as Record<string, unknown>;
  const num = (v: unknown, dp = 0): string | null => (typeof v === 'number' ? v.toFixed(dp) : null);
  const active = beans.find((b) => b.active);
  const alerts = st.events.filter((e) => e.kind === 'notification');
  const fault = alerts.find(isFault);
  return (
    <>
      <Group header="Today">
        <Fact label="Shots pulled">{st.shotsToday}</Fact>
        <Fact label="Yours">
          {st.mineToday} · {st.caffeineTodayMg} mg
        </Fact>
      </Group>
      <Group header="Machine">
        {typeof system.operationMode === 'string' && <Fact label="Mode">{MODE_LABEL[system.operationMode] ?? system.operationMode}</Fact>}
        {num(sensors.temperature, 1) && <Fact label="Boiler">{num(sensors.temperature, 1)} °C</Fact>}
        {typeof profile.name === 'string' && <Fact label="Profile">{profile.name}</Fact>}
        {num(sensors.waterLevel) && <Fact label="Water tank">{num(sensors.waterLevel)}%</Fact>}
        {num(system.livePowerWatts) && <Fact label="Power now">{num(system.livePowerWatts)} W</Fact>}
        {active && <Row to={`/coffee/beans/${active.id}`} title={active.name} sub={beanLine(active, today)} />}
      </Group>
      {Object.keys(maint).length > 0 && (
        <Group header="Upkeep">
          <Fact label="Backflushed">
            {ago(maint.lastBackflushTimestamp, today)} · {num(maint.shotsSinceBackflush) ?? '?'} shots
          </Fact>
          <Fact label="Descaled">
            {ago(maint.lastDescaleTimestamp, today)} · {num(maint.shotsSinceDescale) ?? '?'} shots
          </Fact>
        </Group>
      )}
      {alerts.length > 0 && (
        <Group header="Alerts">
          {fault && <Row tone="bad" title={fault.message} sub={when(fault.at, today)} />}
          {alertsOpen ? (
            alerts.slice(0, 10).map((e, i) => <Row key={i} tone={isFault(e) ? 'bad' : 'default'} title={e.message} sub={when(e.at, today)} />)
          ) : (
            <Row title={`${alerts.length} recent alerts`} sub={`Last: ${alerts[0]!.message}`} onClick={() => setAlertsOpen(true)} />
          )}
        </Group>
      )}
    </>
  );
}

/** Every shot as a compact table, filterable and sortable, one section per day. */
function ShotTable({ shots, beans, today, onMine }: { shots: Shot[]; beans: Bean[]; today: string; onMine: () => Promise<unknown> }): JSX.Element {
  const nav = useNavigate();
  const toast = useToast();
  const [bean, setBean] = useState<number | null>(null);
  const [profile, setProfile] = useState<string | null>(null);
  const [tasted, setTasted] = useState<boolean | null>(null);
  const [sort, setSort] = useState<ShotSort>('newest');
  const [all, setAll] = useState(false);
  const [mine, setMine] = useState<Record<number, boolean>>({});
  const beanName = new Map(beans.map((b) => [b.id, b.name]));
  const usedBeans = beans.filter((b) => shots.some((s) => s.beanId === b.id));
  const profiles = [...new Set(shots.map((s) => s.profileName).filter((p): p is string => !!p))].sort();

  const list = sortShots(filterShots(shots, { bean, profile, tasted }), sort);
  const shown = all ? list : list.slice(0, SHOTS_SHOWN);
  const byTime = sort === 'newest' || sort === 'oldest';
  const sections = byTime ? groupByDay(shown, dayOf) : [{ day: '', shots: shown }];
  const isMine = (s: Shot): boolean => mine[s.id] ?? s.mine;
  const yesterday = ymdInHomeZone(new Date(Date.now() - 86_400_000));
  const dayTitle = (d: string): string => (d === today ? 'Today' : d === yesterday ? 'Yesterday' : dayLabel(d));

  const toggleMine = async (s: Shot): Promise<void> => {
    const next = !isMine(s);
    setMine((m) => ({ ...m, [s.id]: next }));
    try {
      await setShotMine(s.id, next);
      await onMine();
    } catch {
      setMine((m) => ({ ...m, [s.id]: !next }));
      toast.error('Couldn’t change that shot');
    }
  };

  return (
    <section className="cf-shots" aria-label="Shots">
      <div className="cf-filters">
        <div className="ui-chips" role="group" aria-label="Bean">
          <Chip on={bean === null} onClick={() => setBean(null)}>
            All beans
          </Chip>
          {usedBeans.map((b) => (
            <Chip key={b.id} on={bean === b.id} onClick={() => setBean(bean === b.id ? null : b.id)}>
              {b.name}
            </Chip>
          ))}
        </div>
        <div className="cf-filters__selects">
          <select aria-label="Profile" value={profile ?? ''} onChange={(e) => setProfile(e.target.value || null)}>
            <option value="">All profiles</option>
            {profiles.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select aria-label="Tasted" value={tasted === null ? '' : String(tasted)} onChange={(e) => setTasted(e.target.value === '' ? null : e.target.value === 'true')}>
            <option value="">Tasted or not</option>
            <option value="true">Tasted</option>
            <option value="false">Not tasted</option>
          </select>
          <select aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value as ShotSort)}>
            {SORTS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {list.length === 0 ? (
        <EmptyState icon="coffee" title="No shots match." hint="Clear a filter to see more." />
      ) : (
        <div className="cf-table-wrap">
        <table className="cf-table">
          <thead>
            <tr>
              <th scope="col">{byTime ? 'Time' : 'When'}</th>
              <th scope="col">Bean · profile</th>
              <th scope="col" className="num">
                Shot
              </th>
              <th scope="col" className="num">
                Yield
              </th>
              <th scope="col" className="cf-opt">
                Grind
              </th>
              <th scope="col" className="cf-opt">
                Taste
              </th>
              <th scope="col" className="cf-mine">
                I drank it
              </th>
            </tr>
          </thead>
          {sections.map((g) => (
            <tbody key={g.day || 'all'}>
              {g.day && (
                <tr className="cf-table__day">
                  <th colSpan={7} scope="rowgroup">
                    {dayTitle(g.day)}
                    <span>
                      {g.shots.length} {g.shots.length === 1 ? 'shot' : 'shots'}
                      {g.shots.some(isMine) ? ` · ${g.shots.filter(isMine).length} yours` : ''}
                    </span>
                  </th>
                </tr>
              )}
              {g.shots.map((s) => (
                <tr key={s.id} className={isMine(s) ? 'cf-table__row cf-table__row--mine' : 'cf-table__row'} onClick={() => nav(`/coffee/${s.id}`)}>
                  <td>
                    <Link to={`/coffee/${s.id}`} onClick={(e) => e.stopPropagation()}>
                      {byTime ? time(s.startedAt) : when(s.startedAt, today)}
                    </Link>
                  </td>
                  <td className="cf-table__name">
                    {[s.beanId !== null ? beanName.get(s.beanId) : null, s.profileName ?? 'Shot'].filter(Boolean).join(' · ')}
                    {s.mode && s.mode !== 'BREW_AUTO' && s.mode !== 'HOME' && <span className="cf-table__mode">{MODE_LABEL[s.mode] ?? s.mode}</span>}
                  </td>
                  <td className="num">{secs(s.durationMs)}</td>
                  <td className="num">{s.yieldG !== null ? `${s.yieldG} g` : '—'}</td>
                  <td className="cf-opt">{s.grind ?? ''}</td>
                  <td className="cf-opt cf-table__taste">{tasteLine(s)}</td>
                  <td className="cf-mine" onClick={(e) => e.stopPropagation()}>
                    {s.caffeineMg !== null && (
                      <label title={isMine(s) ? `Counted: ${s.caffeineMg} mg` : 'Not counted'}>
                        <input type="checkbox" checked={isMine(s)} onChange={() => void toggleMine(s)} aria-label={`I drank the ${time(s.startedAt)} shot`} />
                      </label>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          ))}
        </table>
        </div>
      )}
      {list.length > shown.length && (
        <button type="button" className="lo-more" onClick={() => setAll(true)}>
          Show {list.length - shown.length} more shots
        </button>
      )}
    </section>
  );
}

function activeProfile(st: { state: Record<string, { payload: unknown }> } | null | undefined): ProfileGuide | undefined {
  const p = st?.state['profile/active']?.payload as { name?: unknown } | undefined;
  return guideFor(typeof p?.name === 'string' ? p.name : null);
}

/** What each profile is for — a reference to glance at, not settings. */
function ProfileGuideGrid({ active }: { active?: ProfileGuide }): JSX.Element {
  return (
    <>
      <div className="cf-guide-grid">
        {PROFILE_GUIDE.map((g) => (
          <article key={g.name} className={`cf-guide-card${g === active ? ' cf-guide-card--on' : ''}`}>
            <h3>
              {g.name}
              {g === active ? <span>In use</span> : g.source === 'community' ? <span>Community</span> : null}
            </h3>
            <p>{g.how}</p>
            <p>
              <b>Best for</b> {g.bestFor}
            </p>
            <p>
              <b>Expect</b> {g.expect}
            </p>
          </article>
        ))}
      </div>
      <p className="cf-foot">Community profiles: github.com/Zer0-bit/gaggiuino (community branch) — import in the machine's web UI.</p>
    </>
  );
}

function GuideRow({ g, detail }: { g: ProfileGuide; detail?: string }): JSX.Element {
  return (
    <Row
      className="cf-guide"
      tone={detail === 'In use' ? 'accent' : 'default'}
      title={g.name}
      sub={
        <>
          <span>{g.how}</span>
          <span>Best for: {g.bestFor}</span>
          <span>Expect: {g.expect}</span>
        </>
      }
      detail={detail}
    />
  );
}

function subline(st: { configured: boolean; state: Record<string, { payload: unknown; updatedAt: string }> }): string {
  if (!st.configured) return 'Not connected';
  const s = st.state.status?.payload;
  return s === 'online' ? 'Online' : s === 'offline' ? 'Offline' : 'Waiting for the machine';
}

/** One shot (`/coffee/:id`): its curves, numbers, and whether it was mine. */
export function CoffeeShot(): JSX.Element {
  const { id = '' } = useParams();
  const shot = useLoad(() => getShot(id), [id]);
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const today = ymdInHomeZone(new Date());
  const back = { to: '/coffee', label: 'Coffee' };

  return (
    <Page
      title={shot.data ? (shot.data.profileName ?? 'Shot') : ''}
      sub={shot.data ? when(shot.data.startedAt, today) : undefined}
      back={back}
    >
      <Loader state={shot}>
        {(s) => (
          <>
            {s.samples.length > 1 && <ShotChart samples={s.samples} />}
            <Group header="Shot">
              <Fact label="Time">{secs(s.durationMs)}</Fact>
              {s.doseG !== null && <Fact label="Dose">{s.doseG} g</Fact>}
              {s.yieldG !== null && <Fact label="Yield">{s.yieldG} g{s.doseG ? ` (1:${(s.yieldG / s.doseG).toFixed(1)})` : ''}</Fact>}
              {s.waterPumpedMl !== null && <Fact label="Water pumped">{s.waterPumpedMl} ml</Fact>}
              {s.peakPressure !== null && <Fact label="Peak pressure">{s.peakPressure} bar</Fact>}
              {s.avgTemp !== null && (
                <Fact label="Temperature">
                  {s.avgTemp} °C{s.targetTemp ? ` (target ${s.targetTemp})` : ''}
                </Fact>
              )}
              {s.mode && <Fact label="Mode">{MODE_LABEL[s.mode] ?? s.mode}</Fact>}
              <Fact label="Caffeine">{!s.mine ? 'not counted' : s.caffeineMg !== null ? `~${s.caffeineMg} mg` : 'none (not a drink)'}</Fact>
            </Group>
            <Tasting shot={s} onSaved={() => shot.reload()} />
            {guideFor(s.profileName) && (
              <Group header="About this profile">
                <GuideRow g={guideFor(s.profileName)!} />
              </Group>
            )}
            {s.caffeineMg !== null && (
              <Button
                variant="secondary"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await setShotMine(s.id, !s.mine);
                    toast.info(s.mine ? 'Not yours — caffeine removed' : 'Marked as yours — caffeine counted');
                    await shot.reload();
                  } catch {
                    toast.error('Couldn’t change that shot');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {s.mine ? 'I didn’t drink this one' : 'I drank this one'}
              </Button>
            )}
          </>
        )}
      </Loader>
    </Page>
  );
}

/** How a shot tasted: bean, grind, tags, rating, notes — and what that says to change. */
function Tasting({ shot: s, onSaved }: { shot: ShotDetail; onSaved: () => Promise<unknown> }): JSX.Element {
  const beans = useLoad(() => listBeans(), []);
  const toast = useToast();
  const [beanId, setBeanId] = useState(s.beanId);
  const [grind, setGrind] = useState(s.grind ?? '');
  const [taste, setTaste] = useState(s.taste);
  const [rating, setRating] = useState(s.rating);
  const [notes, setNotes] = useState(s.tasteNotes ?? '');
  const [busy, setBusy] = useState(false);
  const dirty =
    beanId !== s.beanId || grind !== (s.grind ?? '') || taste.join() !== s.taste.join() || rating !== s.rating || notes !== (s.tasteNotes ?? '');
  const advice = adviceFor({ ...s, taste });
  const toggle = (k: string): void => setTaste((t) => (t.includes(k) ? t.filter((x) => x !== k) : [...t, k]));

  const save = async (e?: FormEvent): Promise<void> => {
    e?.preventDefault();
    setBusy(true);
    try {
      await updateShot(s.id, { beanId, grind: grind.trim() || null, taste, rating, tasteNotes: notes.trim() || null });
      toast.info('Tasting saved');
      await onSaved();
    } catch {
      toast.error('Couldn’t save the tasting');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="cf-tasting" onSubmit={save} aria-label="Tasting">
      <Group header="Tasting">
        <li className="ui-swipe">
          <label className="bc-field">
            <span className="bc-field__label">Bean</span>
            <select className="bc-field__input" value={beanId ?? ''} onChange={(e) => setBeanId(e.target.value === '' ? null : Number(e.target.value))}>
              <option value="">None</option>
              {(beans.data ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
        </li>
        <li className="ui-swipe">
          <label className="bc-field">
            <span className="bc-field__label">Grind</span>
            <input className="bc-field__input" value={grind} placeholder="Grinder setting" onChange={(e) => setGrind(e.target.value)} />
          </label>
        </li>
        <li className="ui-swipe">
          <label className="bc-field bc-field--stack">
            <span className="bc-field__label">Notes</span>
            <textarea className="bc-field__input" value={notes} placeholder="What you noticed — first sip, finish, with milk…" onChange={(e) => setNotes(e.target.value)} />
          </label>
        </li>
      </Group>
      <p className="cf-tasting__label">How was it?</p>
      <div className="ui-chips" role="group" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <Chip key={n} on={rating === n} onClick={() => setRating(rating === n ? null : n)} label={`${n} of 5`}>
            {'★'.repeat(n)}
          </Chip>
        ))}
      </div>
      <p className="cf-tasting__label">What did you taste?</p>
      <div className="ui-chips" role="group" aria-label="Tastes">
        {TASTES.map((t) => (
          <Chip key={t.key} on={taste.includes(t.key)} onClick={() => toggle(t.key)}>
            {t.label}
          </Chip>
        ))}
      </div>
      {taste.length > 0 && (
        <Group header="What that means">
          {taste.map((k) => tasteFor(k)).filter((t): t is NonNullable<typeof t> => !!t).map((t) => (
            <Row key={t.key} className="cf-guide" title={t.label} sub={t.feels} />
          ))}
          {advice.map((a, i) => (
            <Row key={i} className="cf-guide" tone={i === 0 ? 'accent' : 'default'} title={i === 0 ? 'Next shot' : 'Also'} sub={a} />
          ))}
        </Group>
      )}
      <Button type="submit" disabled={busy || !dirty}>
        Save tasting
      </Button>
    </form>
  );
}

const EMPTY_BEAN: BeanInput = { name: '', roaster: '', origin: '', roastLevel: null, roastDate: null, bagNotes: '', notes: '', active: true };

/** One bean (`/coffee/beans/:id`, or `new`): its details and every shot pulled with it — the dial-in log. */
export function CoffeeBean(): JSX.Element {
  const { id = 'new' } = useParams();
  const isNew = id === 'new';
  const nav = useNavigate();
  const toast = useToast();
  const beans = useLoad(() => listBeans(), []);
  const shots = useLoad(() => (isNew ? Promise.resolve([]) : listShots({ bean: Number(id), limit: 200 })), [id]);
  const bean = isNew ? null : beans.data?.find((b) => String(b.id) === id) ?? null;
  const [form, setForm] = useState<BeanInput>(EMPTY_BEAN);
  const [busy, setBusy] = useState(false);
  const today = ymdInHomeZone(new Date());

  useEffect(() => {
    if (bean) setForm({ ...bean });
  }, [bean?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: keyof BeanInput) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const blank = (v: string | null | undefined): string | null => (v ?? '').trim() || null;

  const save = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      const saved = await saveBean(isNew ? null : Number(id), {
        name: (form.name ?? '').trim(),
        roaster: blank(form.roaster),
        origin: blank(form.origin),
        roastLevel: form.roastLevel || null,
        roastDate: form.roastDate || null,
        bagNotes: blank(form.bagNotes),
        notes: blank(form.notes),
        active: form.active,
      });
      toast.info(isNew ? 'Bean added' : 'Bean saved');
      if (isNew) nav(`/coffee/beans/${saved.id}`, { replace: true });
      else await beans.reload();
    } catch {
      toast.error('Couldn’t save the bean');
    } finally {
      setBusy(false);
    }
  };

  const field = (label: string, k: keyof BeanInput, placeholder = ''): JSX.Element => (
    <li className="ui-swipe">
      <label className="bc-field">
        <span className="bc-field__label">{label}</span>
        <input className="bc-field__input" value={String(form[k] ?? '')} placeholder={placeholder} onChange={set(k)} />
      </label>
    </li>
  );

  return (
    <Page title={isNew ? 'New bean' : bean?.name ?? ''} sub={bean ? beanLine(bean, today) : undefined} back={{ to: '/coffee', label: 'Coffee' }}>
      <form className="cf-tasting" onSubmit={save} aria-label="Bean">
        <Group header="Bean">
          {field('Name', 'name', 'Neighborly Blend')}
          {field('Roaster', 'roaster', 'Roasters Next Door')}
          {field('Origin', 'origin', 'Blend, Nicaragua…')}
          <li className="ui-swipe">
            <label className="bc-field">
              <span className="bc-field__label">Roast</span>
              <select className="bc-field__input" value={form.roastLevel ?? ''} onChange={(e) => setForm((f) => ({ ...f, roastLevel: e.target.value || null }))}>
                <option value="">Unknown</option>
                {ROAST_LEVELS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
          </li>
          <li className="ui-swipe">
            <label className="bc-field">
              <span className="bc-field__label">Roasted on</span>
              <input type="date" className="bc-field__input" value={form.roastDate ?? ''} onChange={(e) => setForm((f) => ({ ...f, roastDate: e.target.value || null }))} />
            </label>
          </li>
          {field('Bag says', 'bagNotes', 'Caramel, chocolate, fruit')}
          <li className="ui-swipe">
            <label className="bc-field bc-field--stack">
              <span className="bc-field__label">Notes</span>
              <textarea className="bc-field__input" value={form.notes ?? ''} placeholder="What you think of it" onChange={set('notes')} />
            </label>
          </li>
          <li className="ui-swipe">
            <label className="bc-field">
              <span className="bc-field__text">
                In the hopper now
                <span className="bc-field__hint">New shots are filed under this bean.</span>
              </span>
              <input type="checkbox" className="bc-switch" checked={form.active === true} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />
            </label>
          </li>
        </Group>
        <Button type="submit" disabled={busy || !(form.name ?? '').trim()}>
          {isNew ? 'Add bean' : 'Save'}
        </Button>
      </form>
      {!isNew && (
        <Loader state={shots}>
          {(items) => (
            <Group header="Shots with this bean" footer={items.length ? 'Oldest at the bottom — read up to see how the dial-in went.' : undefined}>
              {items.map((s) => (
                <Row
                  key={s.id}
                  to={`/coffee/${s.id}`}
                  title={[s.grind ? `Grind ${s.grind}` : null, secs(s.durationMs), s.yieldG !== null ? `${s.yieldG} g` : null].filter(Boolean).join(' · ')}
                  sub={[when(s.startedAt, today), s.tasteNotes].filter(Boolean).join(' — ')}
                  detail={[s.rating !== null ? '★'.repeat(s.rating) : null, s.taste.map((k) => tasteFor(k)?.label ?? k).join(', ') || null].filter(Boolean).join(' ') || 'not tasted'}
                />
              ))}
            </Group>
          )}
        </Loader>
      )}
    </Page>
  );
}
